import React from 'react';
import { Button, Loader, NumberInput, SegmentedControl, Select, TextInput } from '@mantine/core';
import { useAuth } from '../../../contexts/AuthProvider';
import {
  creditPaymentsService,
  type ReconcileExternalCreditResponse,
} from '../../../services/credit-payments/credit-payments.service';
import {
  adminSessionsService,
  type ClosedSessionReconcileOption,
} from '../../../services/admin-sessions/admin-sessions.service';
import classes from '../monitoring-page.module.scss';

type ZCreditReconcilePanelProps = {
  tenantId: string | null;
  employeeSession: string | null;
};

function todayIsoDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toDDMMYYYY(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return value;
  }
  const [, yyyy, mm, dd] = match;
  return `${dd}/${mm}/${yyyy}`;
}

function formatDateTime(value: string | null): string {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAmount(value: number | null): string {
  if (typeof value !== 'number') {
    return '-';
  }
  return `${value.toFixed(1)} ₪`;
}

function formatSessionOption(session: ClosedSessionReconcileOption): string {
  return `${session.name} — ${formatDateTime(session.openTime)} עד ${formatDateTime(session.closingTime)}`;
}

export const ZCreditReconcilePanel: React.FC<ZCreditReconcilePanelProps> = ({
  tenantId,
  employeeSession,
}) => {
  const { availableTenants } = useAuth();
  const [rangeType, setRangeType] = React.useState<'date' | 'session'>('date');
  const [fromDate, setFromDate] = React.useState<string>(todayIsoDate());
  const [fromTime, setFromTime] = React.useState<string>('00:00');
  const [toDate, setToDate] = React.useState<string>(todayIsoDate());
  const [toTime, setToTime] = React.useState<string>('23:59');
  const [selectedTenantId, setSelectedTenantId] = React.useState<string | null>(tenantId);
  const [closedSessions, setClosedSessions] = React.useState<ClosedSessionReconcileOption[]>([]);
  const [selectedSessionId, setSelectedSessionId] = React.useState<string | null>(null);
  const [isLoadingSessions, setIsLoadingSessions] = React.useState(false);
  const [matchWindowMinutes, setMatchWindowMinutes] = React.useState<number | string>(30);
  const [localFilter, setLocalFilter] = React.useState('');
  const [activeTab, setActiveTab] = React.useState<'matches' | 'missingExternal' | 'missingLocal'>('matches');
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<ReconcileExternalCreditResponse | null>(null);

  React.useEffect(() => {
    if (!tenantId) {
      return;
    }
    const tenantAllowed = availableTenants.some((tenant) => tenant.tenantId === tenantId);
    if (tenantAllowed) {
      setSelectedTenantId(tenantId);
    }
  }, [tenantId, availableTenants]);

  const tenantOptions = React.useMemo(
    () => availableTenants.map((tenant) => ({ value: tenant.tenantId, label: tenant.companyName })),
    [availableTenants],
  );

  React.useEffect(() => {
    if (rangeType !== 'session' || !selectedTenantId) {
      setClosedSessions([]);
      setSelectedSessionId(null);
      return;
    }

    let active = true;
    setIsLoadingSessions(true);
    setSelectedSessionId(null);
    adminSessionsService
      .listClosedReconcileOptions(selectedTenantId)
      .then((sessions) => {
        if (active) {
          setClosedSessions(sessions);
        }
      })
      .catch((loadError: unknown) => {
        if (active) {
          setClosedSessions([]);
          setError(loadError instanceof Error ? loadError.message : 'טעינת ההפעלות נכשלה');
        }
      })
      .finally(() => {
        if (active) {
          setIsLoadingSessions(false);
        }
      });

    return () => {
      active = false;
    };
  }, [rangeType, selectedTenantId]);

  const sessionOptions = React.useMemo(
    () => closedSessions.map((session) => ({ value: String(session.id), label: formatSessionOption(session) })),
    [closedSessions],
  );

  const canRun = Boolean(
    selectedTenantId
      && !isLoading
      && (rangeType === 'date'
        ? fromDate && fromTime && toDate && toTime
        : selectedSessionId && !isLoadingSessions),
  );

  const runReconcile = async () => {
    if (!selectedTenantId) {
      setError('נדרש לבחור חברה כדי להריץ התאמה.');
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const payload = rangeType === 'date'
        ? {
            rangeType: 'date' as const,
            fromDate: toDDMMYYYY(fromDate),
            fromTime,
            toDate: toDDMMYYYY(toDate),
            toTime,
            ...(typeof matchWindowMinutes === 'number' ? { matchWindowMinutes } : {}),
          }
        : {
            rangeType: 'session' as const,
            sessionId: Number(selectedSessionId),
            ...(typeof matchWindowMinutes === 'number' ? { matchWindowMinutes } : {}),
          };
      const response = await creditPaymentsService.reconcileExternal(
        selectedTenantId,
        payload,
        employeeSession ?? undefined,
      );
      setResult(response);
      setActiveTab('matches');
    } catch (runError: unknown) {
      setError(runError instanceof Error ? runError.message : 'הרצת ההתאמה נכשלה');
    } finally {
      setIsLoading(false);
    }
  };

  const normalizedFilter = localFilter.trim().toLowerCase();
  const matches = result?.matches ?? [];
  const missingInExternal = result?.missingInExternal ?? [];
  const missingInLocal = result?.missingInLocal ?? [];

  const filteredMatches = matches.filter((row) =>
    JSON.stringify(row).toLowerCase().includes(normalizedFilter),
  );
  const filteredMissingInExternal = missingInExternal.filter((row) =>
    JSON.stringify(row).toLowerCase().includes(normalizedFilter),
  );
  const filteredMissingInLocal = missingInLocal.filter((row) =>
    JSON.stringify(row).toLowerCase().includes(normalizedFilter),
  );

  return (
    <div className={classes.zcreditPanel}>
      <div className={classes.filtersCard}>
        <div className={classes.zcreditGrid}>
          <Select
            label="חברה"
            data={tenantOptions}
            value={selectedTenantId}
            onChange={setSelectedTenantId}
            placeholder="בחר חברה"
            searchable
            data-testid="monitoring-zcredit-tenant"
          />
          <SegmentedControl
            data={[
              { value: 'date', label: 'לפי תאריך ושעה' },
              { value: 'session', label: 'לפי הפעלה' },
            ]}
            value={rangeType}
            onChange={(value) => {
              setRangeType(value as 'date' | 'session');
              setError(null);
              setResult(null);
            }}
            data-testid="monitoring-zcredit-range-type"
          />
          {rangeType === 'date' ? (
            <>
              <TextInput
                label="מתאריך"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.currentTarget.value)}
                data-testid="monitoring-zcredit-from-date"
              />
              <TextInput
                label="שעת התחלה"
                type="time"
                value={fromTime}
                onChange={(event) => setFromTime(event.currentTarget.value)}
                data-testid="monitoring-zcredit-from-time"
              />
              <TextInput
                label="עד תאריך"
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.currentTarget.value)}
                data-testid="monitoring-zcredit-to-date"
              />
              <TextInput
                label="שעת סיום"
                type="time"
                value={toTime}
                onChange={(event) => setToTime(event.currentTarget.value)}
                data-testid="monitoring-zcredit-to-time"
              />
            </>
          ) : (
            <Select
              label="הפעלה סגורה"
              data={sessionOptions}
              value={selectedSessionId}
              onChange={setSelectedSessionId}
              placeholder={isLoadingSessions ? 'טוען הפעלות...' : 'בחר הפעלה'}
              disabled={!selectedTenantId || isLoadingSessions}
              searchable
              data-testid="monitoring-zcredit-session"
            />
          )}
          <NumberInput
            label="חלון זמן התאמה (דקות)"
            value={matchWindowMinutes}
            onChange={setMatchWindowMinutes}
            min={0}
            max={1440}
            data-testid="monitoring-zcredit-match-window"
          />
        </div>
        <div className={classes.filtersActions}>
          <Button onClick={() => void runReconcile()} disabled={!canRun} loading={isLoading} data-testid="monitoring-zcredit-run">
            {'הרץ התאמת ZCredit'}
          </Button>
        </div>
      </div>

      {!selectedTenantId && <p className={classes.emptyState}>{'לא נבחרה חברה.'}</p>}
      {error && <p className={classes.error}>{error}</p>}

      {isLoading && (
        <div className={classes.loadingWrap}>
          <Loader />
        </div>
      )}

      {result && (
        <>
          <div className={classes.zcreditSummaryGrid}>
            <div className={classes.zcreditStatCard}><span>רשומות מקומיות</span><strong>{result.summary.localCount}</strong></div>
            <div className={classes.zcreditStatCard}><span>רשומות ZCredit</span><strong>{result.summary.externalCount}</strong></div>
            <div className={classes.zcreditStatCard}><span>התאמות</span><strong>{result.summary.matchedCount}</strong></div>
            <div className={classes.zcreditStatCard}><span>חסר ב-ZCredit</span><strong>{result.summary.missingInExternalCount}</strong></div>
            <div className={classes.zcreditStatCard}><span>חסר במקומי</span><strong>{result.summary.missingInLocalCount}</strong></div>
          </div>

          <div className={classes.filtersCard}>
            <SegmentedControl
              value={activeTab}
              onChange={(value) => setActiveTab(value as 'matches' | 'missingExternal' | 'missingLocal')}
              data={[
                { value: 'matches', label: `התאמות (${filteredMatches.length})` },
                { value: 'missingExternal', label: `חסר ב-ZCredit (${filteredMissingInExternal.length})` },
                { value: 'missingLocal', label: `חסר במקומי (${filteredMissingInLocal.length})` },
              ]}
              size="xs"
              data-testid="monitoring-zcredit-result-tab"
            />
            <TextInput
              label="סינון מקומי בתוצאות"
              placeholder="חיפוש לפי טרנזקציה/אישור/RRN/סכום/4 ספרות"
              value={localFilter}
              onChange={(event) => setLocalFilter(event.currentTarget.value)}
              data-testid="monitoring-zcredit-filter"
            />
          </div>

          {activeTab === 'matches' && (
            <div className={classes.tableWrap}>
              <h3 className={classes.zcreditSubTitle}>{'התאמות'}</h3>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>{'Rule'}</th>
                    <th>{'Local Txn'}</th>
                    <th>{'Order'}</th>
                    <th>{'Local Ref/Approval'}</th>
                    <th>{'Local RRN/Last4'}</th>
                    <th>{'ZCredit Txn'}</th>
                    <th>{'ZCredit Approval/RRN'}</th>
                    <th>{'ZCredit Last4/Status'}</th>
                    <th>{'Local Amount'}</th>
                    <th>{'ZCredit Amount'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMatches.map((row) => (
                    <tr key={`${row.local.transactionId}-${row.external.transactionId ?? 'none'}-${row.rule}`}>
                      <td>{row.rule}</td>
                      <td>{row.local.transactionId}</td>
                      <td>{row.local.orderId}</td>
                      <td>{`${row.local.referenceNumber ?? '-'} / ${row.local.approvalNumber ?? '-'}`}</td>
                      <td>{`${row.local.rrn ?? '-'} / ${row.local.cardLast4 ?? '-'}`}</td>
                      <td>{row.external.transactionId ?? '-'}</td>
                      <td>{`${row.external.approvalNumber ?? '-'} / ${row.external.rrn ?? '-'}`}</td>
                      <td>{`${row.external.cardLast4 ?? '-'} / ${row.external.statusText ?? row.external.status}`}</td>
                      <td>{formatAmount(row.local.amount)}</td>
                      <td>{formatAmount(row.external.amount)}</td>
                    </tr>
                  ))}
                  {filteredMatches.length === 0 && (
                    <tr><td colSpan={10} className={classes.empty}>{'אין התאמות להצגה.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'missingExternal' && (
            <div className={classes.tableWrap}>
              <h3 className={classes.zcreditSubTitle}>{'חסר ב-ZCredit'}</h3>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>{'Transaction'}</th>
                    <th>{'Order'}</th>
                    <th>{'Amount'}</th>
                    <th>{'Created'}</th>
                    <th>{'Ref/Approval'}</th>
                    <th>{'RRN/Last4'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMissingInExternal.map((row) => (
                    <tr key={row.transactionId}>
                      <td>{row.transactionId}</td>
                      <td>{row.orderId}</td>
                      <td>{formatAmount(row.amount)}</td>
                      <td>{formatDateTime(row.createdAt)}</td>
                      <td>{`${row.referenceNumber ?? '-'} / ${row.approvalNumber ?? '-'}`}</td>
                      <td>{`${row.rrn ?? '-'} / ${row.cardLast4 ?? '-'}`}</td>
                    </tr>
                  ))}
                  {filteredMissingInExternal.length === 0 && (
                    <tr><td colSpan={6} className={classes.empty}>{'אין חריגות.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'missingLocal' && (
            <div className={classes.tableWrap}>
              <h3 className={classes.zcreditSubTitle}>{'חסר במקומי'}</h3>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>{'ZCredit Txn'}</th>
                    <th>{'Amount'}</th>
                    <th>{'Occurred'}</th>
                    <th>{'Approval/RRN'}</th>
                    <th>{'Last4/Status'}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMissingInLocal.map((row, index) => (
                    <tr key={`${row.transactionId ?? 'none'}-${index}`}>
                      <td>{row.transactionId ?? '-'}</td>
                      <td>{formatAmount(row.amount)}</td>
                      <td>{formatDateTime(row.occurredAt)}</td>
                      <td>{`${row.approvalNumber ?? '-'} / ${row.rrn ?? '-'}`}</td>
                      <td>{`${row.cardLast4 ?? '-'} / ${row.statusText ?? row.status}`}</td>
                    </tr>
                  ))}
                  {filteredMissingInLocal.length === 0 && (
                    <tr><td colSpan={5} className={classes.empty}>{'אין חריגות.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
